import React from 'react';
import { Loader2, Check, FlaskConical, Info, KeyRound, Trash2 } from 'lucide-react';
import { moduleApi, adminApi } from '../../lib/api';
import { useConfirm } from '../ui/ConfirmProvider';

/** Plateforme agréée telle qu'elle est administrée côté super-admin (global_settings.einvoicingPlatforms). */
interface EInvoicingPlatform {
    code: string;
    nom: string;
    statut?: 'libelle_seul' | 'integration_active';
    url_doc?: string;
}

interface EInvoicingConfig {
    platform_code: string;
    platform_custom_name: string;
    api_key_last4: string;
    configured: boolean;
    configured_at: string | null;
}

const EMPTY: EInvoicingConfig = { platform_code: '', platform_custom_name: '', api_key_last4: '', configured: false, configured_at: null };

/** Onglet « Facturation électronique » des Paramètres — socle de configuration.
 *  L'établissement choisit sa plateforme agréée (liste administrée par le super-admin + option « Autre »)
 *  et y renseigne sa clé API. La clé n'est jamais renvoyée en clair par le serveur : on n'affiche que ses
 *  4 derniers caractères. La génération Factur-X et le circuit d'envoi viendront dans un lot ultérieur ;
 *  le bouton « Tester la connexion » reste donc désactivé tant qu'aucune intégration réelle n'existe. */
export const EInvoicingSettings: React.FC = () => {
    const [loading, setLoading] = React.useState(true);
    const confirmAction = useConfirm();
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);
    const [error, setError] = React.useState('');

    const [platforms, setPlatforms] = React.useState<EInvoicingPlatform[]>([]);
    const [config, setConfig] = React.useState<EInvoicingConfig>(EMPTY);
    const [platformCode, setPlatformCode] = React.useState('');
    const [customName, setCustomName] = React.useState('');
    const [apiKey, setApiKey] = React.useState('');

    React.useEffect(() => {
        Promise.all([
            moduleApi.getEInvoicingSettings().catch(() => EMPTY),
            adminApi.getGlobalSettings().catch(() => ({})),
        ]).then(([cfg, global]: any[]) => {
            const list: EInvoicingPlatform[] = Array.isArray(global?.einvoicingPlatforms) ? global.einvoicingPlatforms : [];
            setPlatforms(list.filter((p) => p && p.code && p.nom));
            const merged = { ...EMPTY, ...(cfg || {}) };
            setConfig(merged);
            setPlatformCode(merged.platform_code || '');
            setCustomName(merged.platform_custom_name || '');
        }).finally(() => setLoading(false));
    }, []);

    const selectedPlatform = platforms.find((p) => p.code === platformCode);
    const isAutre = platformCode === 'autre';
    // Un test de connexion n'a de sens que quand une intégration existe réellement avec la plateforme.
    // Tant que ce n'est pas le cas, le bouton reste visible mais inactif — jamais de faux « OK ».
    const integrationActive = selectedPlatform?.statut === 'integration_active';

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            const payload: { platform_code: string; platform_custom_name?: string; api_key?: string } = {
                platform_code: platformCode,
                platform_custom_name: isAutre ? customName.trim() : '',
            };
            // Clé envoyée seulement si l'utilisateur en a saisi une nouvelle — sinon on garde l'existante,
            // ce qui permet de changer de plateforme sans re-taper le secret.
            if (apiKey.trim()) payload.api_key = apiKey.trim();
            await moduleApi.saveEInvoicingSettings(payload);
            const refreshed: any = await moduleApi.getEInvoicingSettings();
            setConfig({ ...EMPTY, ...(refreshed || {}) });
            setApiKey('');
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'enregistrement.");
        } finally {
            setSaving(false);
        }
    };

    const handleClearKey = async () => {
        if (!(await confirmAction({ title: 'Effacer la clé API ?', description: <>La plateforme sélectionnée est conservée ; seule la clé est effacée.</>, confirmLabel: 'Effacer la clé' }))) return;
        setSaving(true);
        setError('');
        try {
            await moduleApi.saveEInvoicingSettings({ platform_code: platformCode, platform_custom_name: isAutre ? customName.trim() : '', api_key: '' });
            const refreshed: any = await moduleApi.getEInvoicingSettings();
            setConfig({ ...EMPTY, ...(refreshed || {}) });
            setApiKey('');
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'effacement.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;
    }

    return (
        <div className="max-w-2xl space-y-5">
            <div className="flex gap-3 p-4 rounded-2xl bg-surface-soft border border-border">
                <Info size={18} className="text-brand-500 shrink-0 mt-0.5" />
                <div className="text-xs text-content-muted leading-relaxed space-y-1">
                    <p>
                        Depuis le 1<sup>er</sup> septembre 2026, toute entreprise française doit pouvoir <strong>recevoir</strong> une
                        facture électronique. L'obligation d'<strong>émettre</strong> ses factures au format électronique
                        s'applique aux PME, TPE et micro-entreprises à partir du <strong>1<sup>er</sup> septembre 2027</strong>.
                    </p>
                    <p>
                        Le portail public gratuit a été abandonné : les factures doivent transiter par une
                        <strong> plateforme agréée</strong> par l'État. Renseignez ici celle que vous utilisez et votre clé d'accès.
                        Votre mise en page et votre numérotation de factures ne changent pas.
                    </p>
                </div>
            </div>

            <div className="p-4 rounded-2xl bg-surface-soft border border-border space-y-4">
                <div className="space-y-1.5">
                    <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Plateforme agréée</label>
                    <select
                        value={platformCode}
                        onChange={(e) => setPlatformCode(e.target.value)}
                        className="w-full bg-surface border border-border rounded-xl px-4 py-3 text-sm text-content outline-none focus:border-brand-500 transition-colors"
                    >
                        <option value="">— Non configurée —</option>
                        {platforms.map((p) => (
                            <option key={p.code} value={p.code}>{p.nom}</option>
                        ))}
                        <option value="autre">Autre (préciser)</option>
                    </select>
                    {platforms.length === 0 && (
                        <p className="text-[11px] text-content-muted">
                            Aucune plateforme n'a encore été référencée. Choisissez « Autre » et saisissez son nom,
                            ou demandez à l'administrateur d'ajouter votre plateforme à la liste.
                        </p>
                    )}
                    {selectedPlatform?.url_doc && (
                        <a href={selectedPlatform.url_doc} target="_blank" rel="noopener noreferrer" className="text-[11px] text-brand-500 hover:underline">
                            Documentation {selectedPlatform.nom} →
                        </a>
                    )}
                </div>

                {isAutre && (
                    <div className="space-y-1.5">
                        <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Nom de la plateforme</label>
                        <input
                            type="text"
                            value={customName}
                            onChange={(e) => setCustomName(e.target.value)}
                            placeholder="Nom de votre plateforme agréée"
                            className="w-full bg-surface border border-border rounded-xl px-4 py-3 text-sm text-content outline-none focus:border-brand-500 transition-colors"
                        />
                    </div>
                )}

                <div className="space-y-1.5">
                    <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Clé API</label>
                    {config.configured && (
                        <p className="flex items-center gap-2 text-xs text-emerald-500 font-semibold">
                            <Check size={13} /> Une clé est enregistrée (se terminant par «&nbsp;{config.api_key_last4}&nbsp;»).
                            Laissez ce champ vide pour la conserver.
                        </p>
                    )}
                    <div className="flex items-center gap-3">
                        <input
                            type="password"
                            value={apiKey}
                            onChange={(e) => setApiKey(e.target.value)}
                            placeholder={config.configured ? '•••••••••••••••• (inchangée)' : 'Collez votre clé API'}
                            autoComplete="off"
                            className="flex-1 bg-surface border border-border rounded-xl px-4 py-3 text-sm text-content outline-none focus:border-brand-500 transition-colors font-mono"
                        />
                        {config.configured && (
                            <button
                                type="button"
                                onClick={handleClearKey}
                                disabled={saving}
                                className="shrink-0 flex items-center gap-1.5 px-3 py-3 rounded-xl border border-border text-content-muted hover:text-red-400 hover:border-red-500/30 text-xs font-bold transition-all disabled:opacity-50"
                            >
                                <Trash2 size={13} /> Effacer
                            </button>
                        )}
                    </div>
                    <p className="text-[11px] text-content-muted flex items-center gap-1.5">
                        <KeyRound size={12} /> La clé est chiffrée avant stockage et n'est jamais réaffichée en clair.
                    </p>
                </div>

                <div className="flex items-center gap-3 pt-1">
                    <button
                        type="button"
                        disabled
                        title="Disponible lorsqu'une intégration avec cette plateforme sera active."
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-content-muted text-xs font-bold opacity-50 cursor-not-allowed"
                    >
                        <FlaskConical size={14} /> Tester la connexion
                    </button>
                    <span className="text-[11px] text-content-muted">
                        {integrationActive
                            ? 'Test bientôt disponible pour cette plateforme.'
                            : "Disponible quand l'intégration technique avec la plateforme sera en place."}
                    </span>
                </div>
            </div>

            {error && <p className="text-xs text-red-400 font-semibold">{error}</p>}

            <div className="flex items-center gap-3">
                <button
                    onClick={handleSave}
                    disabled={saving || (isAutre && !customName.trim())}
                    className="px-6 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold disabled:opacity-50 transition-colors"
                >
                    {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
                {saved && <span className="flex items-center gap-1.5 text-sm text-emerald-500 font-semibold"><Check size={15} /> Enregistré</span>}
            </div>
        </div>
    );
};
